/**
 * Vue différé unitaire — onglets : différés → donnes → arbre des séquences.
 */

import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import type { ContractCode } from './StatistiquesDiffereePage.types.js';
import DiffereStatsSingleDealDrill from './DiffereStatsSingleDealDrill';
import type { EquivMode } from './DiffereStatsEquivModeToggle';
import {
  computePliColumnMaxima,
  DiffereStatsDistinctCell,
  DiffereStatsEntropyCell,
  DiffereStatsTopHumansCell,
  pliColumnSpan,
} from './DiffereStatsDealTableCells';
import { DIFFERE_SUB_TYPE_LABELS } from '../../utils/differeTrainingBatch';

export interface RecentDiffereItem {
  id: number;
  type: string | null;
  debut: string | null;
  fin: string | null;
  participantCount: number;
}

/** Labels d’affichage (enrichit DIFFERE_SUB_TYPE_LABELS). */
const DIFFERE_TYPE_LABELS: Record<string, string> = {
  ...DIFFERE_SUB_TYPE_LABELS,
  attaque: '100% attaque',
  attaqueSC: '100% attaque (s/c)',
  defense: '100% défense',
  maxiAtt: 'Maxi attaque',
  maxiDef: 'Maxi défense',
  mixte: 'Mixte',
  maxi: 'Maxi',
  normal: 'Normal',
};

const ATT_TYPES = new Set(['attaque', 'attaqueSC', 'maxiAtt']);
const DEF_TYPES = new Set(['defense', 'maxiDef']);
const MIXED_TYPES = new Set(['mixte', 'maxi']);

function formatDiffereDate(raw: string | null): string {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR');
}

function differeTypeLabel(type: string | null): string {
  if (!type) return '?';
  return DIFFERE_TYPE_LABELS[type] ?? type;
}

function matchesTypeFilters(
  type: string | null,
  filterAtt: boolean,
  filterDef: boolean,
  filterSc: boolean,
): boolean {
  if (!filterAtt && !filterDef) return true;
  const t = type ?? '';
  if (filterAtt && filterDef) return MIXED_TYPES.has(t);
  if (filterAtt) {
    if (filterSc) return t === 'attaqueSC';
    return ATT_TYPES.has(t);
  }
  if (filterDef) return DEF_TYPES.has(t);
  return true;
}

export interface TrickDepthStats {
  depth: number;
  entropyGrouped: number;
  distinctGrouped: number;
  top3: Array<{ key: string; card: number | null; count: number; frequency: number }>;
  otherSeatsCards?: [number, number, number] | null;
  otherSeatsCardsConsistent?: boolean;
}

export interface DealRow {
  dealIndex: number;
  contract: number;
  isAttackDeal: boolean;
  playerCount: number;
  topXCount: number;
  /** Effectif de la séquence pli 1→depth la plus fréquente (mêmes clés que l'arbre). */
  maxSequenceCount?: number;
  /** Profondeur adaptative (plis) — seuil max(5, ⌈0.25×topX⌉). */
  adaptiveDepth?: number;
  /** Effectif sur la branche dominante à cette profondeur. */
  adaptiveBranchSize?: number;
  adaptivePathKeys?: string[];
  adaptiveStopReason?: string;
  byDepth: TrickDepthStats[];
}

export interface SingleDiffereReport {
  aggregate: {
    dealCount: number;
    byDepth: Array<{ meanEntropyGrouped: number }>;
  };
  deals: DealRow[];
}

const CONTRACT_NAMES: Record<number, string> = {
  1: 'Petite',
  2: 'Garde',
  3: 'Garde sans',
  4: 'Garde contre',
};

type SingleNavTab = 'differes' | 'deals' | 'tree';

export interface StatistiquesDiffereeSingleProps {
  recentDifferes: RecentDiffereItem[];
  recentTotal: number;
  loadingRecent: boolean;
  loadingMoreRecent: boolean;
  hasMoreRecent: boolean;
  onRefreshList: () => void;
  onLoadMore: () => void;
  benchmarkDiffereIds: Set<number>;
  selectedDiffereId: number | null;
  onSelectDiffere: (id: number) => void;
  singleReport: SingleDiffereReport | null;
  loadingSingle: boolean;
  onRecalculate: () => void;
  singleContractTypes: Record<ContractCode, boolean>;
  setSingleContractTypes: React.Dispatch<React.SetStateAction<Record<ContractCode, boolean>>>;
  topX: number;
  depth: number;
  groupEquiv: boolean;
  equivMode: EquivMode;
  equivRequireOthers: boolean;
  equivRequireWinner: boolean;
  lowRankLimit: number;
  contractTypes: string;
  sessionPseudo: string;
}

export default function StatistiquesDiffereeSingle({
  recentDifferes,
  recentTotal,
  loadingRecent,
  loadingMoreRecent,
  hasMoreRecent,
  onRefreshList,
  onLoadMore,
  benchmarkDiffereIds,
  selectedDiffereId,
  onSelectDiffere,
  singleReport,
  loadingSingle,
  onRecalculate,
  singleContractTypes,
  setSingleContractTypes,
  topX,
  depth,
  groupEquiv,
  equivMode,
  equivRequireOthers,
  equivRequireWinner,
  lowRankLimit,
  contractTypes,
  sessionPseudo,
}: StatistiquesDiffereeSingleProps) {
  const selectedDiffere = recentDifferes.find(d => d.id === selectedDiffereId) ?? null;
  const [selectedDealIndex, setSelectedDealIndex] = useState<number | null>(null);
  const [navTab, setNavTab] = useState<SingleNavTab>('differes');
  const [showTopPli1, setShowTopPli1] = useState(false);
  const [filterAtt, setFilterAtt] = useState(false);
  const [filterDef, setFilterDef] = useState(false);
  const [filterSc, setFilterSc] = useState(false);
  const [filterBench, setFilterBench] = useState(false);
  const loadMoreSentinelRef = useRef<HTMLLIElement | null>(null);

  useEffect(() => {
    if ((!filterAtt || filterDef) && filterSc) setFilterSc(false);
  }, [filterAtt, filterDef, filterSc]);

  const filteredDifferes = useMemo(() => {
    return recentDifferes.filter(d => {
      if (!matchesTypeFilters(d.type, filterAtt, filterDef, filterSc)) return false;
      if (filterBench && !benchmarkDiffereIds.has(d.id)) return false;
      return true;
    });
  }, [recentDifferes, filterAtt, filterDef, filterSc, filterBench, benchmarkDiffereIds]);

  /** Keep fetching pages while filters leave the visible list empty. */
  useEffect(() => {
    if (navTab !== 'differes') return;
    if (loadingRecent || loadingMoreRecent || !hasMoreRecent) return;
    if (filteredDifferes.length > 0) return;
    if (!filterAtt && !filterDef && !filterBench) return;
    onLoadMore();
  }, [
    navTab, loadingRecent, loadingMoreRecent, hasMoreRecent,
    filteredDifferes.length, filterAtt, filterDef, filterBench, onLoadMore,
  ]);

  useEffect(() => {
    if (navTab !== 'differes' || !hasMoreRecent || loadingRecent || loadingMoreRecent) return;
    const el = loadMoreSentinelRef.current;
    if (!el) return;
    const root = el.closest('.differe-stats-recent-rows');
    const obs = new IntersectionObserver(
      entries => {
        if (entries.some(e => e.isIntersecting)) onLoadMore();
      },
      { root, rootMargin: '120px', threshold: 0 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [navTab, hasMoreRecent, loadingRecent, loadingMoreRecent, onLoadMore, filteredDifferes.length]);

  const pliMaxima = useMemo(
    () => (singleReport ? computePliColumnMaxima(singleReport.deals, depth) : { maxEntropy: [], maxDistinct: [] }),
    [singleReport, depth],
  );

  /** Changement de différé uniquement — ne pas quitter l'onglet Arbre si seuls topX / équiv. changent. */
  useEffect(() => {
    setSelectedDealIndex(null);
    if (selectedDiffereId != null) setNavTab('deals');
    else setNavTab('differes');
  }, [selectedDiffereId]);

  const selectedDeal = singleReport?.deals.find(d => d.dealIndex === selectedDealIndex) ?? null;

  const selectDiffere = (id: number) => {
    onSelectDiffere(id);
    setNavTab('deals');
  };

  const selectDeal = (dealIndex: number) => {
    setSelectedDealIndex(dealIndex);
    setNavTab('tree');
  };

  return (
    <>
      <nav className="differe-stats-single-nav" aria-label="Navigation stats différé">
        <button
          type="button"
          className={`differe-stats-single-nav-tab${navTab === 'differes' ? ' active' : ''}`}
          onClick={() => setNavTab('differes')}
        >
          1 · Différés
        </button>
        <button
          type="button"
          className={`differe-stats-single-nav-tab${navTab === 'deals' ? ' active' : ''}`}
          disabled={selectedDiffereId == null}
          onClick={() => setNavTab('deals')}
        >
          2 · Donnes
          {selectedDiffereId != null ? ` #${selectedDiffereId}` : ''}
        </button>
        <button
          type="button"
          className={`differe-stats-single-nav-tab${navTab === 'tree' ? ' active' : ''}`}
          disabled={selectedDealIndex == null || selectedDiffereId == null}
          onClick={() => setNavTab('tree')}
        >
          3 · Arbre
          {selectedDealIndex != null ? ` · donne ${selectedDealIndex + 1}` : ''}
        </button>
      </nav>

      {navTab === 'differes' && (
        <section className="admin-card differe-stats-recent-section">
          <div className="differe-stats-toolbar">
            <h3>
              Différés terminés
              {!loadingRecent && (
                <span className="admin-muted differe-stats-recent-count">
                  {' '}({filteredDifferes.length}
                  {filteredDifferes.length !== recentDifferes.length
                    ? ` / ${recentDifferes.length}`
                    : ''}
                  {recentTotal > recentDifferes.length ? ` · ${recentTotal} au total` : ''})
                </span>
              )}
            </h3>
            <div className="differe-stats-params">
              <button type="button" className="admin-btn" onClick={onRefreshList} disabled={loadingRecent}>
                Actualiser
              </button>
            </div>
          </div>

          <div className="differe-stats-list-filters" role="group" aria-label="Filtres différés">
            <button
              type="button"
              className={`differe-stats-filter-toggle${filterAtt ? ' is-on' : ''}`}
              aria-pressed={filterAtt}
              onClick={() => setFilterAtt(v => !v)}
            >
              Attaque
            </button>
            <button
              type="button"
              className={`differe-stats-filter-toggle${filterDef ? ' is-on' : ''}`}
              aria-pressed={filterDef}
              onClick={() => setFilterDef(v => !v)}
            >
              Défense
            </button>
            <button
              type="button"
              className={`differe-stats-filter-toggle${filterSc ? ' is-on' : ''}`}
              aria-pressed={filterSc}
              disabled={!filterAtt || filterDef}
              title={
                !filterAtt
                  ? 'Activez « Attaque » pour filtrer les sur-contrats'
                  : filterDef
                    ? 'Indisponible en mode mixte (attaque + défense)'
                    : 'Uniquement les différés attaque sur-contrats'
              }
              onClick={() => setFilterSc(v => !v)}
            >
              Sur-contrats
            </button>
            <button
              type="button"
              className={`differe-stats-filter-toggle${filterBench ? ' is-on' : ''}`}
              aria-pressed={filterBench}
              disabled={benchmarkDiffereIds.size === 0}
              title={
                benchmarkDiffereIds.size === 0
                  ? 'Aucun différé dans le pool benchmark'
                  : 'Uniquement les différés du pool benchmark ML'
              }
              onClick={() => setFilterBench(v => !v)}
            >
              Bench{benchmarkDiffereIds.size > 0 ? ` (${benchmarkDiffereIds.size})` : ''}
            </button>
            {(filterAtt && filterDef) && (
              <span className="admin-muted differe-stats-filter-hint">mixte / maxi uniquement</span>
            )}
          </div>

          {loadingRecent ? (
            <p className="admin-muted">Chargement…</p>
          ) : recentDifferes.length === 0 ? (
            <p className="admin-muted">Aucun différé terminé trouvé.</p>
          ) : filteredDifferes.length === 0 && !hasMoreRecent && !loadingMoreRecent ? (
            <p className="admin-muted">Aucun différé ne correspond aux filtres.</p>
          ) : (
            <div className="differe-stats-recent-list">
              <div className="differe-stats-recent-header" aria-hidden="true">
                <span className="differe-stats-recent-col-id">Id</span>
                <span className="differe-stats-recent-col-type">Type</span>
                <span className="differe-stats-recent-col-date">Début</span>
                <span className="differe-stats-recent-col-date">Fin</span>
                <span className="differe-stats-recent-col-players">Joueurs</span>
                <span className="differe-stats-recent-col-bench" />
              </div>
              <ul className="differe-stats-recent-rows">
                {filteredDifferes.map(d => {
                  const inBench = benchmarkDiffereIds.has(d.id);
                  return (
                    <li key={d.id}>
                      <button
                        type="button"
                        className={`differe-stats-recent-item ${selectedDiffereId === d.id ? 'active' : ''}`}
                        onClick={() => selectDiffere(d.id)}
                      >
                        <span className="differe-stats-recent-col-id">#{d.id}</span>
                        <span className="differe-stats-recent-col-type" title={d.type ?? undefined}>
                          {differeTypeLabel(d.type)}
                        </span>
                        <span className="differe-stats-recent-col-date">{formatDiffereDate(d.debut)}</span>
                        <span className="differe-stats-recent-col-date">{formatDiffereDate(d.fin)}</span>
                        <span className="differe-stats-recent-col-players">{d.participantCount}</span>
                        <span className="differe-stats-recent-col-bench">
                          {inBench ? <span className="differe-stats-bench-badge" title="Dans le pool benchmark">bench</span> : null}
                        </span>
                      </button>
                    </li>
                  );
                })}
                {(hasMoreRecent || loadingMoreRecent) && (
                  <li ref={loadMoreSentinelRef} className="differe-stats-recent-sentinel">
                    {loadingMoreRecent
                      ? 'Chargement…'
                      : filteredDifferes.length === 0
                        ? 'Recherche dans les pages suivantes…'
                        : ''}
                  </li>
                )}
              </ul>
            </div>
          )}
        </section>
      )}

      {navTab === 'deals' && selectedDiffere && (
        <section className="admin-card">
          <div className="differe-stats-toolbar">
            <h3>Différé #{selectedDiffere.id} — cartes humaines par donne</h3>
            <button type="button" className="admin-btn" disabled={loadingSingle} onClick={onRecalculate}>
              Recalculer
            </button>
          </div>

          {selectedDiffere.type === 'attaqueSC' && (
            <fieldset className="differe-stats-contracts">
              <legend>Contrats affichés</legend>
              {(['P', 'G', 'GS', 'GC'] as ContractCode[]).map(code => (
                <label key={code} className="differe-stats-check">
                  <input
                    type="checkbox"
                    checked={singleContractTypes[code]}
                    onChange={e => setSingleContractTypes(c => ({ ...c, [code]: e.target.checked }))}
                  />
                  {code}
                </label>
              ))}
            </fieldset>
          )}

          {loadingSingle && <p className="admin-muted">Analyse en cours…</p>}

          {singleReport && (
            <>
              <div className="differe-stats-deals-table-scroll">
                <table className="admin-table differe-stats-deals-table">
                  <thead>
                    <tr>
                      <th rowSpan={2}>Donne</th>
                      <th rowSpan={2} title="Donne d'attaque">Att.</th>
                      <th rowSpan={2}>Joueurs</th>
                      <th
                        rowSpan={2}
                        title={`Effectif de la séquence (pli 1→${depth}) la plus fréquente dans le top-${topX} (${groupEquiv ? `équiv. lowRank≤${lowRankLimit}` : 'brut'})`}
                      >
                        Seq. max
                        <span className="differe-stats-seq-max-mode">
                          {groupEquiv ? `éq.≤${lowRankLimit}` : 'brut'}
                        </span>
                      </th>
                      <th
                        rowSpan={2}
                        title={`Profondeur adaptative : marche sur la branche dominante tant que l'effectif ≥ max(5, ⌈0.25×Top⌉), sans plafond lié au depth UI (jusqu'à 18 plis / fin de donne). Affiche « profondeur · effectif ».`}
                      >
                        Prof. adapt.
                        <span className="differe-stats-seq-max-mode">
                          ≥{Math.max(5, Math.ceil(0.25 * topX))}
                        </span>
                      </th>
                      {Array.from({ length: depth }, (_, i) => (
                        <th key={`pli-h-${i}`} colSpan={pliColumnSpan(i, showTopPli1)}>
                          <span className="differe-stats-pli-header">
                            Pli {i + 1}
                            {i === 0 && (
                              <button
                                type="button"
                                className={`admin-btn admin-btn-sm${showTopPli1 ? ' admin-btn-primary' : ''}`}
                                title={showTopPli1 ? 'Masquer le top pli 1' : 'Afficher le top pli 1'}
                                aria-pressed={showTopPli1}
                                onClick={e => {
                                  e.stopPropagation();
                                  setShowTopPli1(v => !v);
                                }}
                              >
                                Top
                              </button>
                            )}
                          </span>
                        </th>
                      ))}
                    </tr>
                    <tr>
                      {Array.from({ length: depth }, (_, i) => (
                        <Fragment key={`sub-${i}`}>
                          <th title="Entropie (bit) — dispersion des cartes/classes">Entropie</th>
                          <th title="Nombre de cartes ou classes distinctes">Classes</th>
                          {i === 0 && showTopPli1 && <th>Top pli 1</th>}
                        </Fragment>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {singleReport.deals.map(deal => (
                      <tr
                        key={deal.dealIndex}
                        className={`differe-stats-deal-row${selectedDealIndex === deal.dealIndex ? ' active' : ''}`}
                        onClick={() => selectDeal(deal.dealIndex)}
                      >
                        <td>{deal.dealIndex + 1}</td>
                        <td>{deal.isAttackDeal ? '✓' : ''}</td>
                        <td>{deal.playerCount}</td>
                        <td
                          className="differe-stats-seq-max-cell"
                          title={
                            deal.maxSequenceCount != null
                              ? `${deal.maxSequenceCount} / ${deal.topXCount} joueurs sur la séquence dominante (profondeur fixe ${depth})`
                              : undefined
                          }
                        >
                          {deal.maxSequenceCount ?? '—'}
                        </td>
                        <td
                          className="differe-stats-adaptive-cell"
                          title={
                            deal.adaptiveDepth != null && deal.adaptiveDepth > 0
                              ? `Consensus jusqu'au pli ${deal.adaptiveDepth} avec ${deal.adaptiveBranchSize} joueurs (seuil max(5, ⌈0.25×${deal.topXCount}⌉)=${Math.max(5, Math.ceil(0.25 * deal.topXCount))}${deal.adaptiveStopReason ? ` ; arrêt: ${deal.adaptiveStopReason}` : ''})`
                              : `Pas de consensus au pli 1${deal.adaptiveStopReason ? ` (${deal.adaptiveStopReason})` : ''}`
                          }
                        >
                          {deal.adaptiveDepth != null && deal.adaptiveDepth > 0 ? (
                            <>
                              <span className="differe-stats-adaptive-depth">pli {deal.adaptiveDepth}</span>
                              <span className="differe-stats-adaptive-sep">·</span>
                              <span className="differe-stats-adaptive-count">{deal.adaptiveBranchSize ?? '—'}</span>
                            </>
                          ) : (
                            '—'
                          )}
                        </td>
                        {Array.from({ length: depth }, (_, i) => {
                          const pli = deal.byDepth[i];
                          const colSpan = pliColumnSpan(i, showTopPli1);
                          if (!pli) {
                            return (
                              <td key={`empty-${deal.dealIndex}-${i}`} colSpan={colSpan}>—</td>
                            );
                          }
                          return (
                            <Fragment key={`pli-${deal.dealIndex}-${i}`}>
                              <DiffereStatsEntropyCell
                                entropy={pli.entropyGrouped}
                                maxEntropy={pliMaxima.maxEntropy[i] ?? 1}
                              />
                              <DiffereStatsDistinctCell
                                distinct={pli.distinctGrouped}
                                maxDistinct={pliMaxima.maxDistinct[i] ?? 1}
                              />
                              {i === 0 && showTopPli1 && (
                                <DiffereStatsTopHumansCell pli={pli} groupEquiv={groupEquiv} />
                              )}
                            </Fragment>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {singleReport.deals.length === 0 && (
                <p className="admin-muted">Aucune donne pour les filtres choisis (contrats, plis en base).</p>
              )}
            </>
          )}
        </section>
      )}

      {navTab === 'tree' && selectedDiffereId != null && selectedDealIndex != null && (
        <section className="differe-stats-single-deal-drill admin-card">
          <DiffereStatsSingleDealDrill
            sessionPseudo={sessionPseudo}
            differeId={selectedDiffereId}
            dealIndex={selectedDealIndex}
            dealLabel={`donne ${selectedDealIndex + 1} (${CONTRACT_NAMES[selectedDeal?.contract ?? 0] ?? '?'})`}
            topX={topX}
            depth={depth}
            groupEquiv={groupEquiv}
            equivMode={equivMode}
            equivRequireOthers={equivRequireOthers}
            equivRequireWinner={equivRequireWinner}
            lowRankLimit={lowRankLimit}
            contractTypes={contractTypes}
            consensusPathKeys={selectedDeal?.adaptivePathKeys ?? []}
            consensusDepth={selectedDeal?.adaptiveDepth ?? 0}
            consensusBranchSize={selectedDeal?.adaptiveBranchSize ?? 0}
            consensusStopReason={selectedDeal?.adaptiveStopReason}
            differeMeta={selectedDiffere
              ? { type: selectedDiffere.type, debut: selectedDiffere.debut }
              : null}
          />
        </section>
      )}
    </>
  );
}
